import { useEffect, useRef } from 'react';

/**
 * Loads the next page when a sentinel scrolls into view.
 * Pass `rootRef` when the list scrolls inside its own container instead of the window.
 *
 * @param {Object} options
 * @param {React.RefObject<Element|null>} options.sentinelRef
 * @param {React.RefObject<Element|null>} [options.rootRef]
 * @param {boolean} options.hasMore
 * @param {boolean} options.isLoading
 * @param {() => void} options.onLoadMore
 * @param {boolean} [options.enabled=true]
 * @param {string} [options.rootMargin='160px']
 */
export function useInfiniteScroll({
  sentinelRef,
  rootRef,
  hasMore,
  isLoading,
  onLoadMore,
  enabled = true,
  rootMargin = '160px',
}) {
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!enabled || !hasMore || isLoading) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const sentinel = sentinelRef?.current;
    const root = rootRef?.current || null;
    if (!sentinel || (rootRef && !root)) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMoreRef.current?.();
        }
      },
      {
        root,
        rootMargin,
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, hasMore, isLoading, rootRef, sentinelRef, rootMargin]);
}
